// Errors in the web app reach Sentry (PLAN §6.9), when the server says where: `GET /api/v1/client-config` hands over
// the DSN (`SENTRY_WEB_DSN`), the environment and the release. Without one, nothing loads. The SDK is loaded on its
// own, after the app has started, so it never weighs on the first screen. Nothing a person typed or sees is sent:
// no request bodies, no breadcrumbs from inputs, addresses without their query.

interface ClientConfig {
  sentry: { dsn: string; environment: string; release: string | null } | null;
}

let started = false;

/** Resolves when the browser has nothing better to do (or after a few seconds, where it can't say). */
const whenIdle = (): Promise<void> =>
  new Promise((resolve) => {
    if ("requestIdleCallback" in window) window.requestIdleCallback(() => resolve(), { timeout: 5000 });
    else setTimeout(resolve, 3000);
  });

export async function startErrorReporting(): Promise<void> {
  if (started || typeof window === "undefined") return;
  started = true;
  // The SDK is the heaviest thing the app loads and nothing needs it to draw a screen: it waits for a quiet moment.
  // An error that happens before it is ready is kept and handed over, so the first seconds aren't blind.
  const early: unknown[] = [];
  const keep = (event: ErrorEvent | PromiseRejectionEvent) => {
    if (early.length < 20) early.push("reason" in event ? event.reason : event.error);
  };
  window.addEventListener("error", keep);
  window.addEventListener("unhandledrejection", keep);
  await whenIdle();
  let config: ClientConfig | null = null;
  try {
    const response = await fetch("/api/v1/client-config", { credentials: "omit" });
    if (response.ok) config = (await response.json()) as ClientConfig;
  } catch {
    window.removeEventListener("error", keep);
    window.removeEventListener("unhandledrejection", keep);
    return;
  }
  if (!config?.sentry) {
    window.removeEventListener("error", keep);
    window.removeEventListener("unhandledrejection", keep);
    return;
  }
  const Sentry = await import("@sentry/browser");
  Sentry.init({
    dsn: config.sentry.dsn,
    environment: config.sentry.environment,
    ...(config.sentry.release ? { release: config.sentry.release } : {}),
    tracesSampleRate: 0,
    integrations: (defaults) => defaults.filter((i) => i.name !== "Breadcrumbs"),
    beforeSend(event) {
      if (event.request?.url) event.request.url = event.request.url.split(/[?#]/)[0];
      if (event.request) {
        delete event.request.data;
        delete event.request.query_string;
        delete event.request.cookies;
      }
      return event;
    },
  });
  window.removeEventListener("error", keep);
  window.removeEventListener("unhandledrejection", keep);
  for (const error of early) Sentry.captureException(error);
}
